import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

export const MetricConfigSchema = z.object({
  drop_threshold: z.number().min(0).max(1),
  spike_threshold: z.number().min(1),
  min_baseline: z.number().int().min(0),
});
export type MetricConfigType = z.infer<typeof MetricConfigSchema>;

export const CHECK_INTERVALS = ['daily', 'hourly'] as const;
export const CheckIntervalEnum = z.enum(CHECK_INTERVALS);
export type CheckIntervalEnum = z.infer<typeof CheckIntervalEnum>;

export const CHECK_INTERVAL_LABELS: Record<CheckIntervalEnum, string> = {
  daily: 'Daily (07:00)',
  hourly: 'Hourly',
};

export const HealthConfigSchema = z.object({
  id: z.string(),
  location_id: z.string(),
  enabled: z.boolean(),
  cameras: MetricConfigSchema.nullable().optional(),
  violations: MetricConfigSchema.nullable().optional(),
  reservations: MetricConfigSchema.nullable().optional(),
  validations: MetricConfigSchema.nullable().optional(),
  baseline_weeks: z.number().int().min(1),
  // Tolerate a missing/unknown check_interval on the response so a rollout window (portal
  // deployed before the API backfills the column, or any legacy row) degrades to the default
  // instead of throwing a ZodError that blanks the whole config and risks clobbering it on Save.
  // The outgoing SaveHealthConfigPayloadSchema stays strict — we always send a valid value.
  check_interval: CheckIntervalEnum.catch('daily'),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});
export type HealthConfigType = z.infer<typeof HealthConfigSchema>;

export const DIAGNOSIS_SEVERITIES = ['info', 'warning', 'critical'] as const;
export const DiagnosisSeverityEnum = z.enum(DIAGNOSIS_SEVERITIES);
export type DiagnosisSeverityEnum = z.infer<typeof DiagnosisSeverityEnum>;

export const DIAGNOSIS_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export const DiagnosisStatusEnum = z.enum(DIAGNOSIS_STATUSES);
export type DiagnosisStatusEnum = z.infer<typeof DiagnosisStatusEnum>;

export const METRIC_STATUSES = [
  'normal',
  'drop',
  'spike',
  'not_configured',
] as const;
export const MetricStatusEnum = z.enum(METRIC_STATUSES);
export type MetricStatusEnum = z.infer<typeof MetricStatusEnum>;

export const SegmentEvaluationSchema = z.object({
  name: z.string(),
  expected: z.number(),
  actual: z.number(),
  status: MetricStatusEnum,
});
export type SegmentEvaluationType = z.infer<typeof SegmentEvaluationSchema>;

export const MetricEvaluationSchema = z.object({
  expected: z.number(),
  actual: z.number(),
  status: MetricStatusEnum,
  segments: z.array(SegmentEvaluationSchema).optional(),
});
export type MetricEvaluationType = z.infer<typeof MetricEvaluationSchema>;

export const DiagnosisSchema = z.object({
  id: z.string(),
  config_id: z.string(),
  location_id: z.string(),
  analysis_date: zodTimeStamp,
  pattern: z.string(),
  severity: DiagnosisSeverityEnum,
  suppressed: z.boolean(),
  cameras: MetricEvaluationSchema.nullable().optional(),
  violations: MetricEvaluationSchema.nullable().optional(),
  reservations: MetricEvaluationSchema.nullable().optional(),
  validations: MetricEvaluationSchema.nullable().optional(),
  status: DiagnosisStatusEnum,
  acknowledged_by: z.string().optional(),
  acknowledged_at: zodTimeStamp.optional(),
  resolved_at: zodTimeStamp.optional(),
  resolution_notes: z.string().optional(),
  notification_sent: z.boolean(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});
export type DiagnosisType = z.infer<typeof DiagnosisSchema>;

export const DiagnosisPageKeySchema = z.object({
  id: z.string(),
  created_at: zodTimeStamp,
});
export type DiagnosisPageKeyType = z.infer<typeof DiagnosisPageKeySchema>;

export const PaginatedDiagnosesSchema = z.object({
  pagination: z.object({
    hasMore: z.boolean(),
    nextKey: DiagnosisPageKeySchema.optional(),
    count: z.number(),
  }),
  data: z.array(DiagnosisSchema),
  debugInfo: z.record(z.string(), z.unknown()).nullable(),
});
export type PaginatedDiagnosesType = z.infer<typeof PaginatedDiagnosesSchema>;

export const SaveHealthConfigPayloadSchema = z.object({
  enabled: z.boolean(),
  cameras: MetricConfigSchema.nullable().optional(),
  violations: MetricConfigSchema.nullable().optional(),
  reservations: MetricConfigSchema.nullable().optional(),
  validations: MetricConfigSchema.nullable().optional(),
  baseline_weeks: z.number().int().min(1),
  check_interval: CheckIntervalEnum,
});
export type SaveHealthConfigPayloadType = z.infer<
  typeof SaveHealthConfigPayloadSchema
>;

export type DiagnosesListParams = {
  limit: number;
  start_key?: string;
  ascending?: boolean;
  status?: DiagnosisStatusEnum;
  severity?: DiagnosisSeverityEnum;
  start_time?: string;
  end_time?: string;
};

export const SEVERITY_CHIP_COLORS: Record<
  DiagnosisSeverityEnum,
  'default' | 'warning' | 'error'
> = {
  info: 'default',
  warning: 'warning',
  critical: 'error',
};

export const STATUS_CHIP_COLORS: Record<
  DiagnosisStatusEnum,
  'default' | 'info' | 'success'
> = {
  open: 'default',
  acknowledged: 'info',
  resolved: 'success',
};

export const METRIC_STATUS_COLORS: Record<
  MetricStatusEnum,
  'success' | 'error' | 'warning' | 'default'
> = {
  normal: 'success',
  drop: 'error',
  spike: 'warning',
  not_configured: 'default',
};

export const PATTERN_LABELS: Record<string, string> = {
  healthy: 'Healthy',
  camera_offline: 'Camera Offline',
  recognition_issue: 'Recognition Issue',
  enforcement_issue: 'Enforcement Issue',
  compliance_improved: 'Compliance Improved',
  validation_issue: 'Validation Issue',
  payment_issue: 'Payment Issue',
  location_wide: 'Location Wide',
  camera_blind: 'Camera Alive But Blind',
  insufficient_data: 'Insufficient Data',
};

export function getPatternLabel(pattern: string): string {
  return PATTERN_LABELS[pattern] ?? pattern.replaceAll('_', ' ');
}
